import React, { useContext, useEffect, useState } from 'react'
import { Button } from '@which/seatbelt'
import { HeartIcon } from '@which/seatbelt/src/components/Icons'
import { dynamicGa4DataLayerPush, getCookie } from '@which/shared'

import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import classNames from 'classnames'

import { FreeAccountSignupModal } from '../../../../shared/components/FreeAccountSignupModal/FreeAccountSignupModal'
import { saveApiUrl } from '../../../../shared/save-api-url'
import { usePageProps } from '../../../../shared/usePageProps'
import { getQueryString } from '../../../../shared/utils/get-query-string'
import { ArticleTemplateContext } from '../../ArticleTemplateContext'
import styles from './Save.module.scss'

export type SaveLocation = 'hero' | 'content' | 'sticky'

export const Save: React.FC<{ location: SaveLocation; variant?: 'button' | 'icon' }> = ({
  location,
  variant = 'button',
}) => {
  const { getAccessToken } = useKindeAuth()
  const { userAccessState } = usePageProps()
  const isLoggedIn = userAccessState?.userLoggedIn ?? false
  const [isPending, setIsPending] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  const {
    meta: { contentId, title },
    savedStatus = { isSaved: false, id: null },
    setSavedStatus = () => {},
    setToast = () => {},
  } = useContext(ArticleTemplateContext)

  const hasWindow = typeof window !== 'undefined'
  const currentHref = hasWindow ? window.location.href : '/'
  const freeAccountSignUpSource = location === 'sticky' ? 'save-article-sticky' : 'save-article'
  const freeAccountRedirectHref = hasWindow
    ? (() => {
        const url = new URL(currentHref)
        url.searchParams.set('freeAccountSignUpSource', freeAccountSignUpSource)
        return url.toString()
      })()
    : `/?freeAccountSignUpSource=${freeAccountSignUpSource}`

  const registerForFreeUrl = `/registration?freeAccount=true&freeArticle=true&redirect=${encodeURIComponent(freeAccountRedirectHref)}`
  const loginUrl = `/login?redirect=${encodeURIComponent(currentHref)}`
  const SAVE_ARTICLE_URL = `${saveApiUrl()}/articles`

  useEffect(() => {
    const signupSource = getQueryString('freeAccountSignUpSource')
    if (isLoggedIn && (signupSource === 'save-article' || signupSource === 'save-article-sticky')) {
      setToast({
        displayToast: true,
        error: false,
        mainText: 'Sign up complete. Welcome to Which?',
      })
    }
    // isLoggedIn resolves asynchronously, so re-run once it becomes true
  }, [isLoggedIn, setToast])

  const saveArticle = async (withTracking: boolean) => {
    if (withTracking) {
      dynamicGa4DataLayerPush({
        event: 'click_save',
        item_text: title,
        item_group: 'saved items',
        item_content_id: contentId,
        item_spec: `article ${location}`,
        action_group: 'add to save',
      })
    }

    if (!isLoggedIn) {
      setModalOpen(true)
      return
    }

    setIsPending(true)
    try {
      const sessionId = getCookie(document?.cookie, 'sessionId')
      const token = (await getAccessToken()) ?? sessionId

      const response = await fetch(SAVE_ARTICLE_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sourceId: contentId }),
      })

      if (!response.ok) {
        throw new Error('Save failed')
      }

      const data = await response.json()
      setSavedStatus({ isSaved: true, id: data.id })
      setToast({
        displayToast: true,
        error: false,
        mainText: 'Article saved',
        linkText: 'View saved articles',
        linkUrl: '/saved#articles',
      })
    } catch (e) {
      setToast({
        displayToast: true,
        error: true,
        mainText: "Sorry, we couldn't save this article.",
      })
    } finally {
      setIsPending(false)
    }
  }

  const removeSavedArticle = async () => {
    setIsPending(true)
    try {
      dynamicGa4DataLayerPush({
        event: 'click_save',
        item_text: title,
        item_group: 'saved items',
        item_content_id: contentId,
        item_spec: `article ${location}`,
        action_group: 'remove from save',
      })

      const sessionId = getCookie(document?.cookie, 'sessionId')
      const token = (await getAccessToken()) ?? sessionId

      const response = await fetch(SAVE_ARTICLE_URL, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: savedStatus.id }),
      })

      if (!response.ok) {
        throw new Error('Delete failed')
      }

      setSavedStatus({ isSaved: false, id: null })
      setToast({
        displayToast: true,
        error: false,
        mainText: 'Article removed from saved items',
        linkText: 'undo',
        onClick: () => saveArticle(false),
      })
    } catch (e) {
      setToast({
        displayToast: true,
        error: true,
        mainText: 'Sorry, there was an error removing this article.',
      })
    } finally {
      setIsPending(false)
    }
  }

  const handleToggle = () => {
    if (isPending) {
      return
    }
    savedStatus.isSaved ? removeSavedArticle() : saveArticle(true)
  }

  const isIconVariant = variant === 'icon'

  return (
    <>
      <Button
        onClick={handleToggle}
        aria-pressed={savedStatus.isSaved}
        aria-label={
          isIconVariant
            ? savedStatus.isSaved
              ? 'Remove article from saved items'
              : 'Save article'
            : undefined
        }
        appearance="secondary"
        className={classNames(isIconVariant ? styles.saveIconButton : styles.saveButton, {
          [isIconVariant ? styles.saveIconButtonSaved : styles.saveButtonSaved]:
            savedStatus.isSaved,
        })}
        enableSpinner={isPending}
        disabled={isPending}
        {...(isIconVariant && { 'data-testid': 'save-icon-button' })}
      >
        <HeartIcon
          selected={savedStatus.isSaved}
          width={16}
          height={16}
          className={styles.heartIcon}
          data-testid="save-heart-icon"
        />
        {!isIconVariant && (savedStatus.isSaved ? 'Saved article' : 'Save article')}
      </Button>

      <FreeAccountSignupModal
        isOpen={modalOpen}
        closeModal={() => setModalOpen(false)}
        loginUrl={loginUrl}
        registerForFreeUrl={registerForFreeUrl}
        loginButtonDataAttributes={{
          'data-which-id': 'article-savebutton-login-link',
          'data-item-group': 'offers popup',
          'data-item-spec': 'article',
        }}
        registerButtonDataAttributes={{
          'data-which-id': 'article-savebutton-free account signup-cta',
          'data-item-group': 'offers popup',
          'data-item-spec': 'article',
        }}
      />
    </>
  )
}
